/**
 * **Der Netzbezug nach Tarif** (HM-167) — HT und NT aus den Tarifzählwerken
 * des Netzzählers (`SiteEnergyResponse.gridImportTariffs`). Gerechnet hat der
 * Server; hier stehen die Wörter und die Stufe der Farbe.
 *
 * **Tarif 1 heißt HT, Tarif 2 NT.** So belegt die Messstellenpraxis in
 * Deutschland 1.8.1 und 1.8.2, und so meldet es die erste Anlage mit zwei
 * Tarifen. Die OBIS-Kennzahl selbst sagt nur „Tarif 1" — deshalb steht die
 * Nummer immer mit dabei, und wer einen Zähler mit anderer Belegung hat, liest
 * an ihr ab, was gemeint ist.
 *
 * **Die Tarife sind Teile des Netzbezugs, kein weiterer Summand.** Beide
 * Zählwerke werden für sich abgelesen; ihre Summe kann um Rundung und
 * Ablesezeitpunkt vom Gesamtbezug abweichen. Sie stehen deshalb als Zeilen
 * darunter und nicht als dessen Rechnung.
 */
import type { ApiComponents } from "@hausmeter/sdk";

export type GridImportTariff = ApiComponents["schemas"]["GridImportTariff"];
type TariffRole = GridImportTariff["role"];

const WORDS: Readonly<Record<TariffRole, { readonly short: string; readonly label: string }>> = {
  energy_import_t1: { short: "HT", label: "HT · Tarif 1" },
  energy_import_t2: { short: "NT", label: "NT · Tarif 2" },
};

/** Helligkeitsstufe der Netzbezugsfarbe: HT voll, NT blasser — dieselbe Größe (ADR 0013). */
const FADE: Readonly<Record<TariffRole, number>> = {
  energy_import_t1: 0,
  energy_import_t2: 0.45,
};

export interface TariffPart {
  readonly role: TariffRole;
  readonly short: string;
  readonly label: string;
  readonly fade: number;
  readonly wh: number | null;
  readonly tariff: GridImportTariff;
}

export function tariffParts(tariffs: readonly GridImportTariff[]): readonly TariffPart[] {
  return tariffs.map((tariff) => ({
    role: tariff.role,
    ...WORDS[tariff.role],
    fade: FADE[tariff.role],
    wh: tariff.value,
    tariff,
  }));
}

/** Die Farbe einer Zeile — die Netzbezugsfarbe, zum Grund der Karte hin gemischt. */
export function tariffColor(fade: number): string {
  const share = Math.round((1 - fade) * 100);
  return share >= 100
    ? "var(--hm-quantity-grid-import)"
    : `color-mix(in srgb, var(--hm-quantity-grid-import) ${share}%, var(--surface-card))`;
}

/** Eine Zeile für die Kachel ohne Kennzahlen-Karten: „HT 12,3 kWh · NT 4,5 kWh". */
export function tariffCaption(parts: readonly TariffPart[], format: (wh: number) => string): string | null {
  if (parts.length === 0) return null;
  return parts.map((part) => `${part.short} ${part.wh === null ? "–" : format(part.wh)} kWh`).join(" · ");
}
